import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { isTicking, isUrgent, tickPressure } from '../../domain/game/duelClock';
import {
    isDuelSoundOn,
    playDuelOutcome,
    playDuelTick,
    primeDuelTick,
    setDuelSoundOn,
} from './duelSounds';

/**
 * The clock, out loud (SPEC-007 PR-7.6).
 *
 * `duelSounds` is the mechanism — the recordings and the preference — and this
 * is the policy: WHEN a sound is owed, and what a reader has to have done before
 * one can be played at all. The split is the usual one, and it is what lets the
 * audio module be tested without a run and the schedule be tested without an
 * `AudioContext`.
 *
 * TWO SCHEDULES, not one. The clock's is a tick per second value inside a
 * window; the round's is a single sound at the moment it ends. They share the
 * switch and the warmed context and nothing else, and holding them apart is what
 * keeps either readable.
 *
 * A TICK IS OWED FROM TEN SECONDS DOWN — twice as early as the screen goes
 * urgent, because those are two different questions. Urgency is about what the
 * reader has to decide; ticking is about the room, and a clock that only starts
 * making a noise once you are out of time is an alarm rather than a clock. The
 * WINDOW and the build are `domain/game/duelClock`; the voice is `duelTick`;
 * this is only the schedule.
 *
 * A TICK IS OWED ONCE PER SECOND VALUE, and that is what the stamp is for. The
 * run's `seconds` is a number on a state object that re-renders for reasons of
 * its own — a tap, a language change, the catalogue arriving — and an effect
 * that played on every render carrying an urgent second would fire twice for the
 * same second. Keyed on the ROUND as well, because two consecutive rounds both
 * pass through five.
 *
 * PRIMED BY THE FIRST TOUCH ANYWHERE ON THE PAGE rather than by the card tap.
 * The card tap is the obvious gesture and it is the wrong one: it ANSWERS the
 * round, so a reader who taps nothing until the clock is at four hears their
 * first round out in silence. A one-shot `pointerdown` on the window catches
 * that reader, and catches the reader who arrived by tapping the tab bar too.
 */
export default function useDuelSound({ run }) {
    // Read once, from storage, at mount. The preference cannot change under this
    // hook from anywhere else — the toggle below is its only writer.
    const [isOn, setIsOn] = useState(isDuelSoundOn);

    useEffect(() => {
        if (!isOn || typeof window === 'undefined') {
            return undefined;
        }

        // ON MOUNT AS WELL AS ON THE FIRST TOUCH, and the two calls do different
        // halves of the same job. Creating an `AudioContext` and decoding into it
        // need no gesture — only STARTING a sound does — so the recordings can be
        // on their way from the moment the screen opens. That matters for the
        // round's own sound rather than for the clock's: a tick is ten seconds
        // after any tap and the bytes are long since in, where a correct answer
        // can land two seconds after the reader arrived, and a stinger that
        // missed its round is worse than one that never played.
        primeDuelTick();

        const prime = () => primeDuelTick();

        window.addEventListener('pointerdown', prime, { once: true, passive: true });

        return () => window.removeEventListener('pointerdown', prime);
    }, [isOn]);

    const status = run?.status;
    const round = run?.round;
    const seconds = run?.seconds;
    const played = useRef(null);
    const ended = useRef(null);

    /**
     * The sound a round ends on, once per ending.
     *
     * Stamped on the ROUND and the status together rather than on the verdict,
     * for the reason the tick is stamped at all: the run object is replaced on
     * every re-render, and a status that is already `over` re-renders several
     * times before the reader presses anything.
     *
     * A win and a loss are one call, because at this level they are one decision
     * — the round was won or it was not — and because the day a lose recording
     * lands it should light up without this scheduler learning anything new.
     */
    useEffect(() => {
        if (!isOn || (status !== 'revealed' && status !== 'over')) {
            return;
        }

        const stamp = `${round}:${status}`;

        if (ended.current === stamp) {
            return;
        }

        ended.current = stamp;
        playDuelOutcome({ isWin: status === 'revealed' });
    }, [isOn, status, round]);

    useEffect(() => {
        if (!isOn || status !== 'asking' || !isTicking(seconds)) {
            return;
        }

        const stamp = `${round}:${seconds}`;

        if (played.current === stamp) {
            return;
        }

        played.current = stamp;
        playDuelTick({
            pressure: tickPressure(seconds),
            isUrgent: isUrgent(seconds),
            // The gentle voice alternates between two pitches, which is what
            // makes it a tick-TOCK rather than a repeated tick. Which second is
            // which is the scheduler's to decide; what each one sounds like is
            // `duelTick`'s.
            isTock: seconds % 2 === 0,
        });
    }, [isOn, status, round, seconds]);

    /**
     * The switch, and the reason the write is out here rather than inside the
     * updater: React may call an updater more than once, and `writeSetting` and
     * a resumed `AudioContext` are both side effects that must happen exactly as
     * often as the reader pressed the button. `useDuelRun` makes the same
     * argument about banking a run.
     */
    const toggle = useCallback(() => {
        const next = !isOn;

        setIsOn(next);
        setDuelSoundOn(next);

        // Inside the reader's own click, which is the only place iOS will let a
        // context resume — so switching the sound on is also what makes the next
        // tick audible.
        if (next) {
            primeDuelTick();
        }
    }, [isOn]);

    return { isOn, toggle };
}
